import React, {Component} from 'react'

import {
    Grid,
    GridCell,
    Spinner,
    css,
    Icon,
    Theme,
    Paginator,
    Select,
    DataBody,
    DataTable,
    DataCell,
    DataRow,
    Button,
} from "@bdfx/mergado-ui-kit"

import {Option} from "@bdfx/mergado-ui-kit/lib/components/Form/Select";

import trans from "trans";
import {MERGADO_APP, superadminGetMergadoProducts} from "app/helpers/api";
import {MergadoProduct} from "first-steps/steps/map-product-elements";
import {iconSize} from "./product-images";
import {AxiosResponse} from "axios";
import dayjs from "dayjs";

type MergadoProductsType = {
    data: MergadoProduct[]
    limit: number
    offset: number
    total_results: number
    page: number
    maxPage: number
    version?: string
}

interface Props {
    selectedProjectId: number
    selectedTemplateId: number
    selectedProductId: number
}

interface State {
    mergadoProducts: MergadoProductsType
    isLoading: boolean
    version: string
}

class MergadoProducts extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            mergadoProducts: null,
            isLoading: false,
            version: null,
        }
    }

    getProductsImages = (page : number, limit : number, version: string) => {
        const {selectedProjectId, selectedTemplateId, selectedProductId} = this.props

        superadminGetMergadoProducts(selectedProjectId, selectedTemplateId, selectedProductId, page, limit, version)
            .then((response: AxiosResponse<any>) => {
                this.setState({
                    mergadoProducts: response.data,
                    version: response.data.version
                })
            })
            .catch(error => {
                console.error(error.response)
            })
            .finally(() => {
                this.setState({isLoading: false})
            })
    }

    componentDidMount() {
        this.getProductsImages(1, DEFAULT_PAGE_LIMIT, this.state.version)
    }

    handlePageChange = (page) => {
        this.getProductsImages(page, this.state.mergadoProducts.limit ?? DEFAULT_PAGE_LIMIT, this.state.version)
    }

    downloadProduct = (product: MergadoProduct) => {
        const url = window.URL.createObjectURL(new Blob([JSON.stringify(product)]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${product.id}.json`);
        document.body.appendChild(link);
        link.click();
    }

    render() {
        const {isLoading, mergadoProducts, version} = this.state

        if (isLoading) {
            return <div>Getting products info <Spinner type={'bubbles'} /></div>
        }

        if (!mergadoProducts || !mergadoProducts.data) {
            return <div />
        }

        const productsPerPage : Option[] = [25, DEFAULT_PAGE_LIMIT, 100].map((o : number) => ({label: `${o}`, value: `${o}`}))
        const page = mergadoProducts && mergadoProducts.offset && mergadoProducts.limit > 0 ? Math.ceil(mergadoProducts.offset/mergadoProducts.limit) : 1
        const maxPage = mergadoProducts && mergadoProducts.total_results && mergadoProducts.limit > 0 ? Math.ceil(mergadoProducts.total_results/mergadoProducts.limit) : 1
        const versionOptions: Option[] = [
            '2022-09-14',
            '2023-10-10',
            '2024-01-25',
            '2024-07-24',
            dayjs().format("YYYY-MM-DD")
        ].map((o: string) => ({ label: `${o}`, value: `${o}` }))

        return (
            <div key={`results_${this.state.mergadoProducts?.version}_${mergadoProducts.total_results}_${this.state.version}`}>
                {`results_${this.state.mergadoProducts?.version}_${mergadoProducts.total_results}_${this.state.version}`}
                <FiltersGrid>
                    <GridCell>
                        <Paginator
                            showFirstAndLast={true}
                            showPrevAndNext={true}
                            maxLinks={5}
                            labelFirst={`«« ${trans('first')}`}
                            labelPrevious={`« ${trans('previous')}`}
                            labelNext={`${trans('next')} »`}
                            labelLast={`${trans('last')} »»`}
                            currentPage={page}
                            lastPage={maxPage}
                            onPageChange={(page) => this.handlePageChange(page)}
                        />
                    </GridCell>
                    <GridCell>
                        <Select
                            options={productsPerPage}
                            value={`${mergadoProducts.limit}`}
                            onChange={(limit) => {
                                this.getProductsImages(1, limit, version)
                            }}
                        />
                    </GridCell>
                    <GridCell style={{textAlign: 'right'}}>
                        From mergado API {this.state.mergadoProducts?.version} ({mergadoProducts.total_results} products)
                    </GridCell>
                    <GridCell style={{' .muk-form-group': {marginBottom: 0}}}>
                        <Select
                            options={versionOptions}
                            value={version}
                            onChange={(value) => {
                                this.setState({version: value})
                                this.getProductsImages(this.state.mergadoProducts.page, this.state.mergadoProducts.limit ?? DEFAULT_PAGE_LIMIT, value)
                            }}
                        />
                    </GridCell>
                    <GridCell>
                        <Button type={'void'} icon={<Icon type={'refresh'} />} onClick={() => {
                            this.getProductsImages(this.state.mergadoProducts.page, this.state.mergadoProducts.limit ?? DEFAULT_PAGE_LIMIT, this.state.version)
                        }}/>
                    </GridCell>
                </FiltersGrid>

                <StyledProductGrid>
                    {mergadoProducts.data.map((product: MergadoProduct) => {
                        return <GridCell key={`product${product.id}`} className={'shadow-box'}>
                            <div title={`C: ${product.created_at}, U: ${product.output_changed_at}`}>{product.id}</div>

                            <ScrollableDiv title={'data'}>
                                <h5 className={'clickable'} onClick={() => this.downloadProduct(product)}>
                                    <Icon type={'download-light'} color={Theme.steelblue} size={18} text={'data'} />
                                </h5>
                                <DataTable>
                                    <DataBody>
                                        {typeof product.data === "object" && typeof product.data.elements === "undefined" &&
                                        Object.keys(product.data).map((key, i) => <DataRow key={`${product.id}_data_${key}_${i}`}>
                                                <DataCell>{key}</DataCell>
                                                <DataCell>{product.data[key]}</DataCell>
                                            </DataRow>
                                        )}
                                        {typeof product.data?.elements === "object" &&
                                        Object.keys(product.data.elements).map((key, i) => <DataRow key={`${product.id}_data_${key}_${i}`}>
                                                <DataCell>{key}</DataCell>
                                                <DataCell>{JSON.stringify(product.data.elements[key])}</DataCell>
                                            </DataRow>
                                        )}
                                    </DataBody>
                                </DataTable>
                            </ScrollableDiv>

                            {typeof product.input_data === "object" && typeof product.input_data.elements === "undefined" &&
                            <ScrollableDiv title={'input_data'}>
                                <h5>input data</h5>
                                <DataTable>
                                    <DataBody>
                                        {Object.keys(product.input_data).map((key, i) => <DataRow key={`${product.id}_input_data_${key}_${i}`}>
                                                <DataCell>{key}</DataCell>
                                                <DataCell>{product.data[key]}</DataCell>
                                            </DataRow>
                                        )}
                                    </DataBody>
                                </DataTable>
                            </ScrollableDiv>
                            }

                            <div>
                            <a href={`${MERGADO_APP}/${product.id}`} target={'_blank'} className={'links'} title={trans('link_mergado')}>
                                <Icon type={'mergado'} size={iconSize} />
                            </a>
                            </div>
                        </GridCell>
                    })}
                </StyledProductGrid>
            </div>
        )
    }
}

const ScrollableDiv = css('div')({
    width: '100%',
    height: '200px',
    overflow: 'auto',
    background: '#ffffff0a',
    marginBottom: '4px',
    border: '1px solid gray',
    ' td': {
        fontsize: '11px',
        padding: '2px',
    }
})

const StyledProductGrid = css(Grid)({
    gridTemplateColumns: `repeat(auto-fit, minmax(600px, 1fr))`,
    gridGap: '5px',
    marginTop: '20px',
    ' .muk-gridcell': {
        height: '100%',
        padding: '10px',
        backgroundColor: '#c0c0c0',
        alignSelf: 'center',
        textAlign: 'center',
        verticalAlign: 'middle',
        cursor: 'pointer',
        ':hover': {
            backgroundColor: '#efefef',
        },
    },
    ' img.product-image': {
        width: 'auto',
        height: 'auto',
        maxWidth: '100%',
        maxHeight: '100%',
        border: '1px solid gray',
    },
    ' img.template-image': {
        width: 'auto',
        height: 'auto',
        maxWidth: '100%',
        maxHeight: '100%',
    },
    ' .links': {
        padding: 10,
    },
    ' .template-is-active': {
        fontWeight: 'bold',
    },
    ' .active': {
        color: Theme.green,
    },
    ' .inactive': {
        color: Theme.orange,
    }
})

const FiltersGrid = css(Grid)({
    gridTemplateColumns: '1fr 100px 1fr 200px 60px',
    gridGap: '5px',
    ' .muk-gridcell': {
        alignSelf: 'center',
    }
})

const DEFAULT_PAGE_LIMIT = 9

export default MergadoProducts